import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";

// For formatting numbers in our funnel
const formatter = Intl.NumberFormat("en", { notation: "compact" });

const useStyles = makeStyles((theme) => ({
  baseContainer: {
    minHeight: 120,
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
  },
  funnelContainer: {
    padding: 5,
    width: "40%",
    display: "flex",
    fontWeight: "bold",
    alignItems: "center",
    justifyContent: "space-between",
    marginRight: theme.shape.padding,
  },
  funnelTitle: {
    fontSize: 11,
    marginBottom: 0,
    paddingRight: theme.shape.padding,
  },
  dataText: {
    fontSize: 35,
    fontWeight: 400,
    lineHeight: "0.9em",
    marginTop: 6,
    marginBottom: 0,
    wordBreak: "break-all",
  },
  baseFunnel: {
    width: "25%",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
  },
  firstFunnel: {
    borderTopLeftRadius: 5,
    borderBottomLeftRadius: 5,
    padding: `10px 0px 10px ${theme.shape.padding}px`,
    backgroundColor: theme.palette.grey[50],
    color: theme.palette.common.black,
  },
  secondFunnel: {
    marginLeft: -20,
    padding: `10px 0px 10px ${theme.shape.padding + 20}px`,
    backgroundColor: theme.palette.grey[100],
    color: theme.palette.common.black,
  },
  thirdFunnel: {
    marginLeft: -20,
    padding: `10px 0px 10px ${theme.shape.padding + 20}px`,
    backgroundColor: theme.palette.grey[400],
    color: theme.palette.common.black,
  },
  fourthFunnel: {
    color: "white",
    marginLeft: -20,
    padding: `10px 0px 10px ${theme.shape.padding + 20}px`,
    backgroundColor: "#EA4336", // color from designs
    borderTopRightRadius: 5,
    borderBottomRightRadius: 5,
  },
  triangleRight: {
    width: 0,
    height: 0,
    zIndex: 999,
    borderTop: "55px solid transparent",
    borderBottom: "55px solid transparent",
  },
  lightGreyBackground: {
    borderLeft: `20px solid ${theme.palette.grey[50]}`,
  },
  mediumGreyBackground: {
    borderLeft: `20px solid ${theme.palette.grey[100]}`,
  },
  darkGreyBackground: {
    borderLeft: `20px solid ${theme.palette.grey[400]}`,
  },
}));

const FunnelVisualization = (props) => {
  const classes = useStyles();
  const [metrics, setMetrics] = useState({
    findings: 0,
    logsTotal: 0,
    blockedEvents: 0,
    threatsAndSuspects: 0,
  });
  useEffect(() => {
    setMetrics({
      findings: formatter.format(props.findings),
      logsTotal: formatter.format(props.logsTotal),
      blockedEvents: formatter.format(props.blockedEvents),
      threatsAndSuspects: formatter.format(props.threats + props.suspects),
    });
  }, [
    props.findings,
    props.logsTotal,
    props.blockedEvents,
    props.threats,
    props.suspects,
  ]);

  return (
    <div
      className={`${classes.baseContainer} ${classes.funnelContainer}`}
      datacy={"funnelVisualization"}
    >
      <div className={`${classes.baseFunnel} ${classes.firstFunnel}`}>
        <p className={classes.funnelTitle}>Raw logs imported last 6 months</p>
        <p className={classes.dataText}>{metrics.logsTotal}</p>
      </div>

      <div
        className={`${classes.triangleRight} ${classes.lightGreyBackground}`}
      />

      <div className={`${classes.baseFunnel} ${classes.secondFunnel}`}>
        <p className={classes.funnelTitle}>Blocked events</p>
        <p className={classes.dataText}>{metrics.blockedEvents}</p>
      </div>

      <div
        className={`${classes.triangleRight} ${classes.mediumGreyBackground}`}
      />

      <div className={`${classes.baseFunnel} ${classes.thirdFunnel}`}>
        <p className={classes.funnelTitle}>Suspects identified</p>
        <p className={classes.dataText}>{metrics.threatsAndSuspects}</p>
      </div>

      <div
        className={`${classes.triangleRight} ${classes.darkGreyBackground}`}
      />

      <div className={`${classes.baseFunnel} ${classes.fourthFunnel}`}>
        <p className={classes.funnelTitle}>Unresolved findings generated</p>
        <p className={classes.dataText}>{metrics.findings}</p>
      </div>
    </div>
  );
};

FunnelVisualization.propTypes = {
  ready: PropTypes.bool.isRequired,
  findings: PropTypes.number.isRequired,
  logsTotal: PropTypes.number.isRequired,
  blockedEvents: PropTypes.number.isRequired,
  threats: PropTypes.number.isRequired,
  suspects: PropTypes.number.isRequired,
};

FunnelVisualization.defaultProps = {
  ready: false,
  findings: 0,
  logsTotal: 0,
  blockedEvents: 0,
  threats: 0,
  suspects: 0,
};

export default FunnelVisualization;
